import { useState, useEffect, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "../../../contexts/DataContext";
import axios from "axios";
import toast from "react-hot-toast";
import Footer from "../common/footer";
import "../css/main.css";
import Header from "../common/header";

function PlanCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const data = useData();

  const queryParams = new URLSearchParams(location.search);
  const queryParamsPlan = queryParams.get("plan") || "";
  const queryParamsPeriod = queryParams.get("period") || "monthly";
  const queryCheckout = queryParams.get("checkout");
  const queryReference = queryParams.get("reference");
  const microcreditId = queryParams.get("microcredit_id");

  const [formData, setFormData] = useState({
    company_name: "",
    company_location: "",
    company_nuit: "",
    name: "",
    last_name: "",
    email: "",
    contact: "",
    password: "",
    plan: queryParamsPlan,
    period: queryParamsPeriod,
    quantity: microcreditId ? "1" : "1",
  });

  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [paymentWaiting, setPaymentWaiting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentTimeout, setPaymentTimeout] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [subscriptionReference, setSubscriptionReference] = useState(
    queryReference || ""
  );
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});

  // ===== PLAN SELECTION UI STATE =====
  const [showPlanSelector, setShowPlanSelector] = useState(false);

  // ===== PAYMENT METHOD STATE =====
  const [paymentMethod, setPaymentMethod] = useState(""); // "mpesa" | "emola"
  const [methodTouched, setMethodTouched] = useState(false);

  // ===== PLANS STATE =====
  const [planDetailsMap, setPlanDetailsMap] = useState({});
  const [availablePlans, setAvailablePlans] = useState([]);

  useEffect(() => {
    document.querySelector("body").scrollIntoView({ behavior: "instant" });
  }, []);

  useEffect(() => {
    const required_data = ["plans"];
    data.handleLoaded("remove", required_data);
    data._get(required_data);
  }, [location.pathname]);

  // ===== BUILD PLAN DETAILS MAP =====
  useEffect(() => {
    try {
      const details = {};
      const plansArr = [];
      data._plans.forEach((i) => {
        const planObj = { ...i };
        planObj.price = data._cn(i.price).replaceAll(",00", "");
        planObj.features = (i.features || "")
          .replaceAll("\n", "")
          .split(";")
          .filter((f) => f);
        details[i.name.toLowerCase()] = planObj;
        plansArr.push(i.name.toLowerCase());
      });
      setPlanDetailsMap(details);
      setAvailablePlans(plansArr);
    } catch (e) {
      // silent
    }
  }, [data._plans, location.pathname]);

  useEffect(() => {
    if (queryReference) {
      setSubscriptionReference(queryReference);
      pollPaymentStatus(queryReference);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryReference]);

  function clearUrlAndState() {
    const url = new URL(window.location);
    url.searchParams.delete("checkout");
    url.searchParams.delete("reference");
    url.searchParams.delete("subscription");
    window.history.replaceState({}, "", url);
  }

  const POLL_TIMEOUT_MS = 120000;
  const POLL_INTERVAL_MS = 5000;

  async function pollPaymentStatus(reference) {
    const startTime = Date.now();

    const doPoll = async () => {
      try {
        let response;
        if (microcreditId) {
          response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/api/microcredit-payment/check`,
            {
              params: { reference, microcredit_id: microcreditId },
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        } else {
          response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/api/subscription/check-payment`,
            { params: { reference } }
          );
        }

        if (response.data.checkoutUrl) {
          setCheckoutUrl(response.data.checkoutUrl);
        }

        if (response.data.status === "active") {
          setPaymentWaiting(false);
          setPaymentTimeout(false);
          setPaymentSuccess(true);
          document.querySelector("body").scrollIntoView({ behavior: "auto" });
          if (response.data.token && response.data.refreshToken) {
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("refresh-token", response.data.refreshToken);
          }
          clearUrlAndState();
          return;
        }

        if (response.data.status === "failed") {
          setPaymentWaiting(false);
          toast.error("O pagamento falhou. Tente novamente.");
          return;
        }

        const elapsed = Date.now() - startTime;
        if (elapsed >= POLL_TIMEOUT_MS) {
          setPaymentTimeout(true);
          setPaymentWaiting(false);
          toast(
            "Tempo de espera esgotado. Clique no botão para retomar o pagamento.",
            { icon: "⏱️" }
          );
          return;
        }

        setTimeout(doPoll, POLL_INTERVAL_MS);
      } catch (e) {
        setPaymentWaiting(false);
        toast.error("Erro ao verificar status do pagamento.");
      }
    };

    setPaymentWaiting(true);
    setPaymentTimeout(false);
    doPoll();
  }

  function reopenCheckout() {
    if (checkoutUrl) {
      window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      if (paymentTimeout) {
        setPaymentTimeout(false);
        setPaymentWaiting(true);
        pollPaymentStatus(subscriptionReference);
      }
    }
  }

  // ===== SELECTED PLAN DERIVED VALUES =====
  const selectedPlanDetails =
    planDetailsMap[(formData.plan || "").toLowerCase()] || null;
  const planFeaturesList = selectedPlanDetails?.features || [];
  const planRawPrice = parseFloat(String(selectedPlanDetails?.price || 0)?.replace(/\./g, "")) || 0;
  const planNameDisplay =
    selectedPlanDetails?.name ||
    (formData.plan ? formData.plan.toUpperCase() : "");

  function getPriceByPeriod(monthlyPrice, period) {
    const price = parseFloat(String(monthlyPrice || 0).replace(/\./g, "")) || 0;
    if (period === "yearly" && price > 0) {
      return (price * 11).toFixed(2).replace(/\.?0+$/, "");
    }
    return price;
  }

  const displayedPrice =
    formData.period === "yearly"
      ? getPriceByPeriod(planRawPrice, "yearly")
      : planRawPrice;

  const qtyNum = parseInt(formData.quantity) || 0;
  const totalPrice = displayedPrice * qtyNum;

  const planNameLabel =
    formData.plan === "free"
      ? "GRÁTIS"
      : planNameDisplay || formData.plan.toUpperCase();

  function changeForm(field, value) {
    if (field === "contact" || field === "company_nuit") {
      if (value.length > 9) value = value.slice(0, 9);
      value = value.replace(/[^0-9]/g, "");
    }
    setFormData({ ...formData, [field]: value });
    setTouched({ ...touched, [field]: true });
  }

  function handleQuantityChange(value) {
    let qty = value;
    if (qty === "") {
      setFormData({ ...formData, quantity: "" });
      return;
    }
    qty = parseInt(qty) || 0;
    if (qty < 0) qty = 0;
    setFormData({ ...formData, quantity: qty });
  }

  function handlePlanChange(newPlanKey) {
    setFormData((prev) => ({ ...prev, plan: newPlanKey }));
    setShowPlanSelector(false);
  }

  const requiredFields = [
    "company_name",
    "company_location",
    "company_nuit",
    "name",
    "last_name",
    "email",
    "contact",
    "password",
  ];

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isFieldInvalid(field) {
    if (!touched[field]) return false;
    const value = formData[field];
    if (!value || value.toString().trim() === "") return true;
    if (field === "contact" && value.length !== 9) return true;
    if (field === "company_nuit" && value.length !== 9) return true;
    if (field === "email" && !emailRegex.test(value.trim())) return true;
    if (field === "password" && value.length < 4) return true;
    return false;
  }

  function inputClass(field, extra = "") {
    const invalid = isFieldInvalid(field);
    return `w-full px-4 py-2.5 border rounded-lg outline-none transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 ${
      invalid
        ? "border-red-500 bg-red-50 dark:bg-red-950/30 focus:ring-2 focus:ring-red-400 focus:border-transparent"
        : "border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    } ${extra}`;
  }

  function markAllTouched() {
    const all = {};
    requiredFields.forEach((f) => (all[f] = true));
    setTouched(all);
  }

  function validatePaymentMethod() {
    setMethodTouched(true);
    if (!paymentMethod) {
      toast.error("Selecione um método de pagamento (M-Pesa ou E-Mola).");
      return false;
    }
    return true;
  }

  // ===== PAYMENT METHOD SELECTOR COMPONENT =====
  const PaymentMethodSelector = ({ compact = false }) => {
    const methods = [
      {
        key: "mpesa",
        label: "M-Pesa",
        logo: (
          <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center text-white font-bold text-xs">
            M
          </div>
        ),
      },
      {
        key: "emola",
        label: "E-Mola",
        logo: (
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center text-white font-bold text-xs">
            E
          </div>
        ),
      },
    ];

    const showError = (methodTouched || compact) && !paymentMethod;

    return (
      <div className={compact ? "" : "mb-6"}>
        {!compact && (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Método de pagamento <span className="text-red-500">*</span>
          </label>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          {methods.map((m) => {
            const isSelected = paymentMethod === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => {
                  setPaymentMethod(m.key);
                  setMethodTouched(true);
                }}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                  isSelected
                    ? m.key === "mpesa"
                      ? "border-red-500 bg-red-50 dark:bg-red-950/30"
                      : "border-orange-500 bg-orange-50 dark:bg-orange-950/30"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                {m.logo}
                <div className="flex-1 text-left">
                  <div
                    className={`font-bold text-sm ${
                      isSelected
                        ? m.key === "mpesa"
                          ? "text-red-700 dark:text-red-400"
                          : "text-orange-700 dark:text-orange-400"
                        : "text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {m.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Pagamento móvel
                  </div>
                </div>
                {isSelected && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="18"
                    viewBox="0 -960 960 960"
                    width="18"
                    className={
                      m.key === "mpesa"
                        ? "fill-red-600 dark:fill-red-400"
                        : "fill-orange-500 dark:fill-orange-400"
                    }
                  >
                    <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
        {showError && (
          <p className="text-xs text-red-500 mt-2">
            Selecione um método de pagamento
          </p>
        )}
      </div>
    );
  };

  // ===== PLAN SELECTOR COMPONENT =====
  const PlanSelector = () => (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Escolha um plano <span className="text-red-500">*</span>
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {availablePlans.filter(i=>i.name!="Free").map((planKey) => {
          const plan = planDetailsMap[planKey];
          if (!plan) return null;
          const isSelected = (formData.plan || "").toLowerCase() === planKey;
          const planPrice = parseFloat(plan.price?.replace(/\./g, "")) || 0;
          return (
            <button
              key={planKey}
              type="button"
              onClick={() => handlePlanChange(planKey)}
              className={`text-left ${planPrice==0 ? 'hidden':''} p-3 rounded-xl border-2 transition-all ${
                isSelected
                  ? "border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`font-bold text-sm ${
                    isSelected
                      ? "text-blue-700 dark:text-blue-400"
                      : "text-gray-800 dark:text-gray-200"
                  }`}
                >
                  {plan.name}
                </span>
                {isSelected && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="16"
                    viewBox="0 -960 960 960"
                    width="16"
                    className="fill-blue-600 dark:fill-blue-400"
                  >
                    <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
                  </svg>
                )}
              </div>
              <div
                className={`text-xs ${
                  isSelected
                    ? "text-blue-600 dark:text-blue-300"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {planPrice === 0 ? "Grátis" : `${planPrice}MT/mês`}
              </div>
              {/***{plan.features && plan.features.length > 0 && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                  {plan.features.slice(0, 2).join(" · ")}
                  {plan.features.length > 2 ? " ..." : ""}
                </div>
              )} */}
            </button>
          );
        })}
      </div>
      {availablePlans.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          A carregar planos...
        </p>
      )}
    </div>
  );

  async function sendData(resend) {
    if (microcreditId) {
      if (!validatePaymentMethod()) return;

      try {
        setLoading(true);
        toast.remove();
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/microcredit-payment/create`,
          {
            microcredit_id: microcreditId,
            plan: formData.plan || queryParamsPlan,
            period: formData.period || queryParamsPeriod,
            quantity: formData.quantity || 1,
            amount: totalPrice,
            method: paymentMethod,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        toast.remove();
        setCheckoutUrl(response.data.checkoutUrl);
        setSubscriptionReference(response.data.reference);
        setLoading(false);

        setPaymentWaiting(true);
        pollPaymentStatus(response.data.reference);
        window.open(response.data.checkoutUrl, "_blank");
      } catch (error) {
        toast.remove();
        let msg = "Erro ao criar pagamento.";
        if (error.response?.data?.message) msg = error.response.data.message;
        toast.error(msg);
        setLoading(false);
      }
      return;
    }

    toast.remove();
    markAllTouched();

    if (!validatePaymentMethod()) return;

    let canPass = true;
    requiredFields.forEach((f) => {
      if (!formData[f] || formData[f].toString().trim() === "")
        canPass = false;
    });
    if (!canPass) {
      toast.error("Preencha todos os dados");
      return;
    }
    if (formData.contact.length !== 9) {
      toast.error("Número de contacto deve ter 9 dígitos.");
      return;
    }
    if (formData.company_nuit.length !== 9) {
      toast.error("Número de nuit deve ter 9 dígitos.");
      return;
    }
    if (!emailRegex.test(formData.email.trim())) {
      toast.error("email invalido");
      return;
    }
    if (formData.password.length < 4) {
      toast.error("A senha deve ter pelo menos 4 caracteres.");
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/send-verification-code`,
        {
          ...formData,
          quantity: formData.quantity || 1,
          method: paymentMethod,
        }
      );
      setConfirm(true);
      toast.remove();
      setLoading(false);

      if (resend === "resend") {
        toast.success("código reenviado!");
      }

      document.querySelector("body").scrollIntoView({ behavior: "auto" });
    } catch (error) {
      toast.remove();
      try {
        toast.error("Correu um: " + error.response.data.message);
      } catch (e) {
        toast.error(
          "Something went wrong, check your internet connection and try again!"
        );
      }
      setLoading(false);
    }
  }

  async function sendConfirmation() {
    toast.remove();
    if (!formData.verification_code) {
      toast.error("Insira o código");
      return;
    }

    if (!paymentMethod) {
      toast.error("Selecione um método de pagamento.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/subscription/create-checkout`,
        {
          ...formData,
          code: formData.verification_code,
          quantity: formData.quantity || 1,
          amount: totalPrice,
          method: paymentMethod,
        }
      );

      toast.remove();
      setCheckoutUrl(response.data.checkoutUrl);
      setSubscriptionReference(response.data.reference);
      setLoading(false);

      setConfirm(false);
      setPaymentWaiting(true);
      pollPaymentStatus(response.data.reference);
      document.querySelector("body").scrollIntoView({ behavior: "auto" });
      window.open(response.data.checkoutUrl, "_blank");
    } catch (e) {
      let msg = "Acorou um erro, tente novamente";
      setLoading(false);

      if (e.response) {
        if (e.response.status === 409) msg = "Código usado ou inválido";
        if (e.response.status === 400) msg = "Dados inválidos";
        if (e.response.status === 500)
          msg = "Erro, inesperado. Contacte seu administrador";
        if (e.response.status === 502)
          msg = "Erro ao criar pagamento. Contacte seu administrador";
      } else if (e.code === "ERR_NETWORK") {
        msg = "Verfique sua internet e tente novamente";
      }

      toast.error(msg);
    }
  }

  function cancelConfirmation() {
    toast.remove();
    setConfirm(false);
    setFormData({ ...formData, verification_code: "" });
    document
      .querySelectorAll(".confirm_container input")
      .forEach((i) => (i.value = ""));
  }

  function resendEmail() {
    toast.remove();
    sendData("resend");
  }

  return (
    <div className="bg-blue-200 dark:bg-gray-950 transition-colors duration-300 min-h-screen">
      <Header />
      <div
        id="plan-checkout"
        className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white dark:from-gray-950 dark:to-gray-950 py-12 px-6 pb-32 lg:pb-12 transition-colors duration-300"
      >
        {!paymentSuccess ? (
          <div className="max-w-6xl mx-auto">
            {/* TITLE */}
            <div className="text-center mb-12 pt-24">
              <h3 className="text-blue-700 dark:text-blue-500 font-bold uppercase tracking-widest text-sm mb-2">
                Checkout
              </h3>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-4">
                {microcreditId
                  ? "Reactivar o seu microcrédito"
                  : "Finalizar a sua subscrição"}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
                {microcreditId
                  ? "Escolha um plano e confirme os dados abaixo para reactivar o seu microcrédito."
                  : "Complete os dados para aderir ao seu plano e começar a usar o VLMS."}
              </p>
            </div>

            {paymentWaiting && (
              <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-gray-800">
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <h3 className="text-xl font-semibold text-blue-700 dark:text-blue-400 mb-4">
                    Aguardando confirmação de pagamento
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Complete o pagamento na aba que foi aberta. Se estiver
                    usando um dispositivo móvel, abra o aplicativo{" "}
                    {paymentMethod === "mpesa" ? "M-Pesa" : "E-Mola"} e confirme
                    a transação.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Esta página monitorará automaticamente o status do seu
                    pagamento.
                  </p>
                </div>
              </div>
            )}

            {paymentTimeout && (
              <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-gray-800">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="32"
                      viewBox="0 -960 960 960"
                      width="32"
                    >
                      <path
                        d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm-1-160v-120h-80v120h-120v80h120v120h80v-120h120v-80h-120Z"
                        className="fill-yellow-500"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">
                    Pagamento pendente
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    O tempo de verificação foi esgotado. Se você completou o
                    pagamento, aguarde alguns minutos e clique em "Verificar
                    novamente" ou use o botão abaixo para retomar o pagamento.
                  </p>
                  {checkoutUrl && (
                    <button
                      onClick={reopenCheckout}
                      className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2 mb-3"
                    >
                      Abrir página de pagamento
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setPaymentTimeout(false);
                      pollPaymentStatus(subscriptionReference);
                    }}
                    className="w-full px-6 py-3 border-2 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-semibold rounded-xl hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-200"
                  >
                    Verificar novamente
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* LEFT COLUMN */}
              <div className="hidden lg:block lg:sticky lg:top-8 lg:self-start lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto lg:pr-1">
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow dark:shadow-black/40 p-8 border border-gray-100 dark:border-gray-800">
                  <div className="mb-6 text-center">
                    <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                      {planNameLabel}
                    </span>
                    <span
                      className={`ml-3 inline-block px-4 py-1 rounded-full text-xs font-semibold ${
                        formData.period === "yearly"
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {formData.period === "yearly" ? "Anual" : "Mensal"}
                    </span>
                  </div>

                  <div className="mb-6 text-center">
                    <span className="text-5xl font-extrabold text-blue-600 dark:text-blue-400">
                      {displayedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}MT
                    </span>
                    <span className="text-lg text-gray-500 dark:text-gray-400 ml-2">
                      {formData.period === "yearly" ? "/ano" : "/mês"}
                    </span>
                  </div>

                  {formData.period === "yearly" && displayedPrice > 0 && (
                    <div className="mb-6 p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-100 dark:border-green-900/50 text-center">
                      <span className="text-green-700 dark:text-green-400 font-semibold">
                        Você paga 11 meses e o 12º mês é grátis!
                      </span>
                    </div>
                  )}

                  <div className="mb-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantidade <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          handleQuantityChange(String(Math.max(0, qtyNum - 1)))
                        }
                        className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="0"
                        value={
                          formData.quantity === "" ? "" : formData.quantity
                        }
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        placeholder="0"
                        className="flex-1 px-4 py-2.5 text-blue-600 dark:text-blue-400 text-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleQuantityChange(String(qtyNum + 1))}
                        className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        +
                      </button>
                    </div>

                    {qtyNum > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-100 dark:border-blue-900/50">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            Total ({qtyNum}x):
                          </span>
                          <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                            {totalPrice.toFixed(2).replace(/\.?0+$/, "").toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}MT
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Payment method summary in sticky panel */}
                  {paymentMethod && (
                    <div className="mb-6 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Método:
                        </span>
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                          {paymentMethod === "mpesa" ? "M-Pesa" : "E-Mola"}
                        </span>
                      </div>
                    </div>
                  )}

                  {planFeaturesList.length > 0 && (
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Recursos incluídos:
                      </h3>
                      {planFeaturesList.map((f, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 py-2 border-b border-gray-50 dark:border-gray-800 last:border-0"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="20"
                            viewBox="0 -960 960 960"
                            width="20"
                          >
                            <path
                              d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z"
                              className="fill-green-500"
                            />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {f}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div id="right" className="space-y-6">
                {!confirm ? (
                  <Fragment>
                    {microcreditId ? (
                      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow dark:shadow-black/40 p-8 border border-gray-100 dark:border-gray-800">
                        <h3 className="text-xl font-semibold text-blue-700 dark:text-blue-400 mb-4">
                          Confirmar Pagamento
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                          Confirme os dados do pagamento para o microcrédito
                          seleccionado.
                        </p>

                        {showPlanSelector && <PlanSelector />}

                        <div className="space-y-4 mb-6">
                          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-600 dark:text-gray-400">
                              Plano:
                            </span>
                            <span className="font-semibold items-center gap-2 flex text-gray-800 dark:text-gray-100">
                              <label>{planNameLabel}</label>
                              {!showPlanSelector && (
                                <button
                                  type="button"
                                  onClick={() => setShowPlanSelector(true)}
                                  className="w-full text-[15px] py-1 px-2 border-2 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 font-semibold rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-200 flex items-center justify-center gap-2"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    height="16"
                                    viewBox="0 -960 960 960"
                                    width="16"
                                    fill="currentColor"
                                  >
                                    <path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z" />
                                  </svg>
                                  Mudar plano
                                </button>
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-600 dark:text-gray-400">
                              Período:
                            </span>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                              <select
                                className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded px-2 py-1 text-sm outline-none"
                                value={formData.period}
                                onChange={(e) =>
                                  changeForm("period", e.target.value)
                                }
                              >
                                <option value="monthly">Mensal</option>
                                <option value="yearly">
                                  Anual (1 mês grátis)
                                </option>
                              </select>
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                            <span className="text-gray-600 dark:text-gray-400">
                              Preço unitário:
                            </span>
                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                              {displayedPrice}MT
                              {formData.period === "yearly" ? "/ano" : "/mês"}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-800 items-center">
                            <span className="text-gray-600 dark:text-gray-400">
                              Quantidade:
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  changeForm(
                                    "quantity",
                                    String(
                                      Math.max(
                                        1,
                                        (parseInt(formData.quantity) || 1) - 1
                                      )
                                    )
                                  )
                                }
                                className="w-7 h-7 rounded border border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300"
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={formData.quantity}
                                onChange={(e) =>
                                  changeForm("quantity", e.target.value)
                                }
                                className="w-12 px-1 py-0.5 text-center border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  changeForm(
                                    "quantity",
                                    String(
                                      (parseInt(formData.quantity) || 1) + 1
                                    )
                                  )
                                }
                                className="w-7 h-7 rounded border border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* ===== PAYMENT METHOD ===== */}
                        <div className="mb-6 pt-4 border-t border-gray-100 dark:border-gray-800">
                          <PaymentMethodSelector />
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 mb-6 text-center border border-blue-100 dark:border-blue-900/50">
                          <span className="text-gray-600 dark:text-gray-400 text-sm">
                            Total
                          </span>
                          <div className="text-3xl font-bold text-blue-700 dark:text-blue-400">
                            {totalPrice.toFixed(2).replace(/\.?0+$/, "")}MT
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <button
                            className="flex-1 py-3 px-6 border-2 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-semibold rounded-xl hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-200"
                            onClick={() => navigate("/")}
                          >
                            Cancelar
                          </button>
                          <div className="flex-1 relative">
                            <button
                              className="w-full py-3 px-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                              onClick={sendData}
                              disabled={
                                loading || !formData.plan || !paymentMethod
                              }
                            >
                              {loading ? (
                                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              ) : (
                                "Pagar"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Fragment>
                        {/* Company Info */}
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow dark:shadow-black/40 p-6 border border-gray-100 dark:border-gray-800">
                          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs">
                              1
                            </span>
                            Informações da Empresa
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Empresa{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.company_name}
                                onChange={(e) =>
                                  changeForm("company_name", e.target.value)
                                }
                                placeholder="Nome da empresa"
                                className={inputClass("company_name")}
                              />
                              {isFieldInvalid("company_name") && (
                                <p className="text-xs text-red-500 mt-1">
                                  Campo obrigatório
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Nuit <span className="text-red-500">*</span>
                              </label>
                              <input
                                value={formData.company_nuit}
                                maxLength="9"
                                onChange={(e) =>
                                  changeForm("company_nuit", e.target.value)
                                }
                                type="number"
                                placeholder="Nuit da empresa"
                                className={inputClass("company_nuit")}
                              />
                              {isFieldInvalid("company_nuit") && (
                                <p className="text-xs text-red-500 mt-1">
                                  {formData.company_nuit
                                    ? "Nuit deve ter 9 dígitos"
                                    : "Campo obrigatório"}
                                </p>
                              )}
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Localização{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.company_location}
                                onChange={(e) =>
                                  changeForm(
                                    "company_location",
                                    e.target.value
                                  )
                                }
                                placeholder="Localização da empresa"
                                className={inputClass("company_location")}
                              />
                              {isFieldInvalid("company_location") && (
                                <p className="text-xs text-red-500 mt-1">
                                  Campo obrigatório
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Personal Data */}
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow dark:shadow-black/40 p-6 border border-gray-100 dark:border-gray-800">
                          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs">
                              2
                            </span>
                            Dados Pessoais
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Nome completo{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.name}
                                onChange={(e) =>
                                  changeForm("name", e.target.value)
                                }
                                placeholder="Seu nome"
                                className={inputClass("name")}
                              />
                              {isFieldInvalid("name") && (
                                <p className="text-xs text-red-500 mt-1">
                                  Campo obrigatório
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Sobrenome{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.last_name}
                                onChange={(e) =>
                                  changeForm("last_name", e.target.value)
                                }
                                placeholder="Seu sobrenome"
                                className={inputClass("last_name")}
                              />
                              {isFieldInvalid("last_name") && (
                                <p className="text-xs text-red-500 mt-1">
                                  Campo obrigatório
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Email <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={formData.email}
                                onChange={(e) =>
                                  changeForm("email", e.target.value)
                                }
                                placeholder="Seu email"
                                className={inputClass("email")}
                              />
                              {isFieldInvalid("email") && (
                                <p className="text-xs text-red-500 mt-1">
                                  {formData.email
                                    ? "Email inválido"
                                    : "Campo obrigatório"}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Contacto{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                value={formData.contact}
                                maxLength="9"
                                onChange={(e) =>
                                  changeForm("contact", e.target.value)
                                }
                                onWheel={(e) => e.target.blur()}
                                type="number"
                                placeholder="Contacto"
                                className={inputClass(
                                  "contact",
                                  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                )}
                              />
                              {isFieldInvalid("contact") && (
                                <p className="text-xs text-red-500 mt-1">
                                  {formData.contact
                                    ? "Contacto deve ter 9 dígitos"
                                    : "Campo obrigatório"}
                                </p>
                              )}
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Senha <span className="text-red-500">*</span>
                              </label>
                              <div className="relative">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  value={formData.password}
                                  onChange={(e) =>
                                    changeForm("password", e.target.value)
                                  }
                                  placeholder="Crie uma senha"
                                  minLength="4"
                                  className={inputClass("password", "pr-12")}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowPassword(!showPassword)
                                  }
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                  aria-label={
                                    showPassword
                                      ? "Ocultar senha"
                                      : "Mostrar senha"
                                  }
                                >
                                  {showPassword ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="20"
                                      viewBox="0 -960 960 960"
                                      width="20"
                                      fill="currentColor"
                                    >
                                      <path d="M607.5-372.5Q660-425 660-500t-52.5-127.5Q555-680 480-680t-127.5 52.5Q300-575 300-500t52.5 127.5Q405-320 480-320t127.5-52.5Zm-204-51Q370-456 370-500t33.5-76.5Q437-610 480-610t76.5 33.5Q590-543 590-500t-33.5 76.5Q523-390 480-390t-76.5-33.5ZM480-200q-151 0-269-83.5T40-500q21-83 77-148.5T254-756l-42-42 56-56 660 660-56 56-118-118q-56 28-130 42t-144 14Zm0-80q57 0 108.5-10.5T676-322L596-402q-23 11-52 16.5t-64 5.5q-100 0-170-70t-70-170q0-35 5.5-64t16.5-52l-80-80q-47 31-93 76T50-500q35 107 132.5 183.5T480-280Zm-40-260Z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      height="20"
                                      viewBox="0 -960 960 960"
                                      width="20"
                                      fill="currentColor"
                                    >
                                      <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29Zm0 200q-151 0-269-83.5T40-500q21-83 77-148.5T254-756q64-44 148-64t218-20q119 0 203 20t149 64q63 44 119 109.5T960-500q-21 83-77 148.5T774-244q-64 44-148 64t-146 20Z" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              {isFieldInvalid("password") && (
                                <p className="text-xs text-red-500 mt-1">
                                  {formData.password
                                    ? "Senha deve ter pelo menos 4 caracteres"
                                    : "Campo obrigatório"}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Payment Method */}
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow dark:shadow-black/40 p-6 border border-gray-100 dark:border-gray-800">
                          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs">
                              3
                            </span>
                            Método de Pagamento
                          </h3>
                          <PaymentMethodSelector />
                        </div>

                        {/* Submit */}
                        <div className="flex gap-4 pt-4">
                          <button
                            className="flex-1 py-3 px-6 border-2 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-semibold rounded-xl hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-200"
                            onClick={() => navigate("/#home")}
                          >
                            Cancelar
                          </button>
                          <div className="flex-1 relative">
                            <button
                              className="w-full py-3 px-6 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                              onClick={() => sendData()}
                              disabled={loading || !paymentMethod}
                            >
                              {loading ? (
                                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              ) : (
                                "Enviar"
                              )}
                            </button>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  </Fragment>
                ) : (
                  /* EMAIL CONFIRMATION */
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow dark:shadow-black/40 p-6 sm:p-8 border border-gray-100 dark:border-gray-800">
                    <div className="text-center mb-6">
                      <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center mx-auto mb-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="28"
                          viewBox="0 -960 960 960"
                          width="28"
                          fill="currentColor"
                          className="text-blue-600 dark:text-blue-400"
                        >
                          <path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z" />
                        </svg>
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        Verifique seu email
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                        Enviamos um código de confirmação para{" "}
                        <strong className="text-blue-600 dark:text-blue-400 break-all">
                          {formData.email}
                        </strong>
                      </p>
                    </div>

                    <div className="mb-5">
                      <input
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            verification_code: e.target.value,
                          })
                        }
                        placeholder="Digite o código"
                        type="text"
                        inputMode="numeric"
                        className="w-full px-4 py-3 text-center text-lg tracking-widest border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>

                    {/* Payment method (compact) */}
                    <div className="mb-5 pt-4 border-t border-gray-100 dark:border-gray-800">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 text-left">
                        Método de pagamento
                      </label>
                      <PaymentMethodSelector compact />
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-3 mb-5">
                      <button
                        className="py-2 px-4 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                        onClick={cancelConfirmation}
                      >
                        Cancelar
                      </button>
                      <button
                        className="py-2 px-4 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                        onClick={resendEmail}
                      >
                        Reenviar código
                      </button>
                      <button
                        className="py-2 px-4 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                        onClick={cancelConfirmation}
                      >
                        Mudar email
                      </button>
                    </div>

                    <div className="flex justify-center">
                      <button
                        className="w-full sm:w-auto py-3 px-8 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2"
                        onClick={sendConfirmation}
                      >
                        {loading ? (
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          `Confirmar e Pagar com ${
                            paymentMethod === "mpesa" ? "M-Pesa" : "E-Mola"
                          }`
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* PAYMENT SUCCESS */
          <div className="min-h-[60vh] flex items-center justify-center px-0 pt-24">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl dark:shadow-black/40 max-w-lg w-full p-10 text-center border border-gray-100 dark:border-gray-800">
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-950/40 flex items-center justify-center mx-auto mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 -960 960 960"
                  width="48"
                  className="fill-green-500"
                >
                  <path d="m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
                </svg>
              </div>

              <h1 className="text-3xl font-bold text-blue-700 dark:text-blue-400 mb-4">
                {microcreditId
                  ? "Microcrédito adicionado com sucesso!"
                  : "Bem-vindo ao VLMS!"}
              </h1>

              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {microcreditId
                  ? "O pagamento do seu microcrédito foi confirmado com sucesso. O valor foi adicionado à sua conta."
                  : "O seu pagamento foi confirmado com sucesso e a sua conta já está pronta a usar. Tudo foi configurado para que possa começar imediatamente."}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => {
                    if (microcreditId) {
                      window.location.href = "/";
                    } else {
                      window.location.href = "/";
                    }
                  }}
                  className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200"
                >
                  Ir para o Dashboard
                </button>
                <button
                  onClick={() => {
                    if (microcreditId) {
                      window.location.href = "/login";
                    } else {
                      navigate("/");
                    }
                  }}
                  className="px-8 py-3 border-2 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-semibold rounded-xl hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-200"
                >
                  {microcreditId ? "Ir para Login" : "Voltar à página inicial"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MOBILE FIXED BOTTOM BAR */}
      {!paymentSuccess && !confirm && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-2xl px-4 py-3 lg:hidden">
          <div className="flex items-center justify-between gap-3 max-w-6xl mx-auto">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={() =>
                  handleQuantityChange(String(Math.max(0, qtyNum - 1)))
                }
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
              >
                −
              </button>
              <input
                type="number"
                min="0"
                value={formData.quantity === "" ? "" : formData.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                placeholder="0"
                className="w-14 px-1 py-1.5 text-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                type="button"
                onClick={() => handleQuantityChange(String(qtyNum + 1))}
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
              >
                +
              </button>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {planNameLabel} ·{" "}
                {formData.period === "yearly" ? "Anual" : "Mensal"}
                {paymentMethod && (
                  <> · {paymentMethod === "mpesa" ? "M-Pesa" : "E-Mola"}</>
                )}
              </div>
              <div className="text-lg font-bold text-blue-700 dark:text-blue-400">
                {totalPrice.toFixed(2).replace(/\.?0+$/, "").toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}MT
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default PlanCheckout;